import React, { useState, useEffect, useRef } from 'react';
import CustomSelect from '../components/shared/CustomSelect';
import { submitDeparture, sendDepartureReport } from '../utils/api';
import { IGLU_ORANGE, IGLU_LOGO_DARK } from '../data/map';
import { cssUrl } from '../utils/cssVersion';

const MATTRESS_OPTIONS = ['Double', 'King Single', '-'];
const ROOM_TYPE_OPTIONS = [
  'Departure Multishared',
  'Departure Studio',
  'Corrective Clean Multishared',
  'Corrective Clean Studio',
  'Steam Clean',
  'Common Area Clean',
];
const YES_NO = ['Yes', 'No'];
const STATUS_OPTIONS = ['Pending'];
const EMAIL_OPTIONS = [
  'vnguyen@iglu.com.au',
  'southyarra@iglu.com.au',
  'sgowty@iglu.com.au',
  'ayeong@iglu.com.au',
];

const ACCENT = IGLU_ORANGE;

const todayStr = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const INITIAL_FORM = {
  requestDate: todayStr(),
  cleanDate: '',
  room: '',
  mattress: '',
  roomType: '',
  bedroom: '',
  bathroom: '',
  kitchen: '',
  cleanStatus: 'Pending',
  turnover: '',
  comms: 'No Comments',
  email: '',
};

const ErrorIcon = () => (
  <svg fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

// ─────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────
const NewDeparture = ({ onSubmitted }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailReport, setEmailReport] = useState(true);

  const lastSectionRef = useRef(null);
  const prevSectionsRef = useRef('');
  const formFieldsRef = useRef(null);

  // ─── Reset to a fresh form (stays on the current tab) ───
  const resetForm = () => {
    setFormData({ ...INITIAL_FORM, requestDate: todayStr() });
    setErrors({});
    setTouched(new Set());
    prevSectionsRef.current = '';
    formFieldsRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ─── Stylesheets — versioned via cssUrl() → /static/reactcss/<file>?v=<mtime> ───
  useEffect(() => {
    const link1 = document.createElement('link');
    link1.rel = 'stylesheet';
    link1.href = cssUrl('newuseronboarding.css');
    document.head.appendChild(link1);
    const link2 = document.createElement('link');
    link2.rel = 'stylesheet';
    link2.href = cssUrl('filterTemplate.css');
    document.head.appendChild(link2);
    return () => {
      document.head.removeChild(link1);
      document.head.removeChild(link2);
    };
  }, []);

  // ─── Toast auto-dismiss ───
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // ─── Visibility (progressive reveal) ───
  const showRoom = !!formData.requestDate && !!formData.cleanDate && !errors.cleanDate;
  const showRoomDetail = showRoom && !!formData.room.trim();
  const showAreas = showRoomDetail && !!formData.mattress && !!formData.roomType;
  const showStatus = showAreas && !!formData.bedroom && !!formData.bathroom && !!formData.kitchen;
  const showEmail = showStatus && !!formData.turnover;
  const showSummary = showEmail && !!formData.email;

  // ─── Gentle scroll when a new section reveals ───
  useEffect(() => {
    const key = [showRoom, showRoomDetail, showAreas, showStatus, showEmail, showSummary]
      .map((v) => (v ? '1' : '0'))
      .join('');
    if (prevSectionsRef.current && key !== prevSectionsRef.current && lastSectionRef.current) {
      setTimeout(
        () => lastSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }),
        150
      );
    }
    prevSectionsRef.current = key;
  }, [showRoom, showRoomDetail, showAreas, showStatus, showEmail, showSummary]);

  // ─── Handlers ───
  const setValue = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => new Set([...prev, field]));
  };

  const clearError = (field) =>
    setErrors((prev) => {
      const n = { ...prev };
      delete n[field];
      return n;
    });

  const handleRequestDateChange = (val) => {
    setValue('requestDate', val);
    if (formData.cleanDate && val && formData.cleanDate < val) {
      setErrors((prev) => ({ ...prev, cleanDate: 'Clean date cannot be before the request date' }));
    } else {
      clearError('cleanDate');
    }
  };

  const handleCleanDateChange = (val) => {
    setValue('cleanDate', val);
    if (val && formData.requestDate && val < formData.requestDate) {
      setErrors((prev) => ({ ...prev, cleanDate: 'Clean date cannot be before the request date' }));
      setToast({ type: 'error', message: 'Clean date cannot be before the request date' });
    } else {
      clearError('cleanDate');
    }
  };

  const handleRoomChange = (e) => {
    const val = e.target.value.toUpperCase();
    setValue('room', val);
    if (!val.trim()) {
      setErrors((prev) => ({ ...prev, room: 'Room number is required' }));
    } else {
      clearError('room');
    }
  };

  const handleCommsChange = (e) => setValue('comms', e.target.value);
  const handleCommsBlur = () => {
    if (!formData.comms.trim()) setValue('comms', 'No Comments');
  };

  const inputClass = (field) =>
    `ob-input ${touched.has(field) && errors[field] ? 'ob-input-error' : ''}`;

  // ─── Validation & submit ───
  const isFormValid = () => {
    const f = formData;
    if (!f.requestDate || !f.cleanDate || !f.room.trim()) return false;
    if (!f.mattress || !f.roomType) return false;
    if (!f.bedroom || !f.bathroom || !f.kitchen) return false;
    if (!f.cleanStatus || !f.turnover || !f.email) return false;
    if (Object.keys(errors).length > 0) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!isFormValid() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        room: formData.room.trim(),
        comms: formData.comms.trim() || 'No Comments',
      };
      await submitDeparture(payload);
      let msg = 'Departure clean submitted successfully!';
      if (emailReport) {
        try {
          await sendDepartureReport([payload], 'form');
          msg += ' PDF emailed.';
        } catch (e) {
          msg += ' (Saved, but the email failed.)';
        }
      }
      setToast({ type: 'success', message: msg });
      resetForm();
      onSubmitted?.();           // parent tab page can switch back to its default tab
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Submission failed' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────
  //  Render
  // ─────────────────────────────────────────────
  return (
    <div className="ob-page">
      {toast && (
        <div className={`ob-toast ${toast.type === 'success' ? 'ob-toast-success' : 'ob-toast-error'}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {toast.type === 'success'
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />}
          </svg>
          {toast.message}
        </div>
      )}

      <div className="ob-container">
        <div className="ob-header">
          <img
            src={IGLU_LOGO_DARK}
            alt="IGLU South Yarra"
            style={{ width: 84, height: 84, objectFit: 'contain', display: 'block', margin: '0 auto 8px' }}
          />
          <h1 className="ob-title">Departure Clean</h1>
          <p className="ob-subtitle">Fill in the details to request a departure or corrective clean</p>
        </div>

        <div className="ob-form-card">
          <div className="ob-form-fields" ref={formFieldsRef} style={{ minHeight: '50vh', maxHeight: '80vh' }}>

            {/* ─── Dates ─── */}
            <div className="ob-field">
              <label className="ob-label">Request Date <span className="ob-label-required">*</span></label>
              <input
                type="date"
                value={formData.requestDate}
                onChange={(e) => handleRequestDateChange(e.target.value)}
                className={inputClass('requestDate')}
              />
            </div>

            <div className="ob-field">
              <label className="ob-label">Clean Date <span className="ob-label-required">*</span></label>
              <input
                type="date"
                value={formData.cleanDate}
                min={formData.requestDate || undefined}
                onChange={(e) => handleCleanDateChange(e.target.value)}
                className={inputClass('cleanDate')}
              />
              {touched.has('cleanDate') && errors.cleanDate && (
                <div className="ob-error"><ErrorIcon />{errors.cleanDate}</div>
              )}
            </div>

            {/* ─── Room ─── */}
            {showRoom && (
              <>
                <div className="ob-divider" />
                <div className="ob-field">
                  <label className="ob-label">Room Number <span className="ob-label-required">*</span></label>
                  <input
                    type="text"
                    value={formData.room}
                    onChange={handleRoomChange}
                    placeholder="MSY-2001"
                    className={inputClass('room')}
                  />
                  {touched.has('room') && errors.room && (
                    <div className="ob-error"><ErrorIcon />{errors.room}</div>
                  )}
                </div>
              </>
            )}

            {showRoomDetail && (
              <>
                <div className="ob-field">
                  <CustomSelect
                    label="Room Type"
                    value={formData.roomType}
                    onChange={(v) => setValue('roomType', v)}
                    options={ROOM_TYPE_OPTIONS}
                  />
                </div>
                <div className="ob-field">
                  <CustomSelect
                    label="Mattress Protector"
                    value={formData.mattress}
                    onChange={(v) => setValue('mattress', v)}
                    options={MATTRESS_OPTIONS}
                  />
                </div>
              </>
            )}

            {/* ─── Areas ─── */}
            {showAreas && (
              <>
                <div className="ob-divider" />
                <div className="ob-field">
                  <CustomSelect label="Bedroom" value={formData.bedroom} onChange={(v) => setValue('bedroom', v)} options={YES_NO} />
                </div>
                <div className="ob-field">
                  <CustomSelect label="Bathroom" value={formData.bathroom} onChange={(v) => setValue('bathroom', v)} options={YES_NO} />
                </div>
                <div className="ob-field">
                  <CustomSelect label="Kitchen or Common Area" value={formData.kitchen} onChange={(v) => setValue('kitchen', v)} options={YES_NO} />
                </div>
              </>
            )}

            {/* ─── Status / Turnover ─── */}
            {showStatus && (
              <>
                <div className="ob-divider" />
                <div className="ob-field">
                  <CustomSelect
                    label="Cleaning Status"
                    value={formData.cleanStatus}
                    onChange={(v) => setValue('cleanStatus', v)}
                    options={STATUS_OPTIONS}
                  />
                </div>
                <div className="ob-field">
                  <CustomSelect label="Same Day Turnover" value={formData.turnover} onChange={(v) => setValue('turnover', v)} options={YES_NO} />
                </div>
              </>
            )}

            {/* ─── Comments / Email ─── */}
            {showEmail && (
              <>
                <div className="ob-divider" />
                <div className="ob-field">
                  <label className="ob-label">Comments</label>
                  <input
                    type="text"
                    value={formData.comms}
                    onChange={handleCommsChange}
                    onBlur={handleCommsBlur}
                    placeholder="No Comments"
                    className={inputClass('comms')}
                  />
                </div>
                <div className="ob-field">
                  <CustomSelect
                    label="Notify Email"
                    value={formData.email}
                    onChange={(v) => setValue('email', v)}
                    options={EMAIL_OPTIONS}
                  />
                </div>
              </>
            )}

            {/* ─── Summary Card ─── */}
            {showSummary && (
              <>
                <div className="ob-divider" />
                <div className="ob-record-card">
                  <div className="ob-record-header">
                    <div className="ob-record-icon" style={{ color: ACCENT }}>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                    </div>
                    <h3 className="ob-record-title">Departure Summary</h3>
                  </div>
                  <div className="ob-record-grid">
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Room</div>
                      <div className="ob-record-item-value" style={{ color: ACCENT }}>{formData.room}</div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Type</div>
                      <div className="ob-record-item-value">{formData.roomType}</div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Dates</div>
                      <div className="ob-record-item-value">{formData.requestDate} → {formData.cleanDate}</div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Mattress</div>
                      <div className="ob-record-item-value">{formData.mattress}</div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Areas</div>
                      <div className="ob-record-item-value">
                        {[
                          formData.bedroom === 'Yes' && 'Bedroom',
                          formData.bathroom === 'Yes' && 'Bathroom',
                          formData.kitchen === 'Yes' && 'Kitchen',
                        ].filter(Boolean).join(', ') || 'None'}
                      </div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Turnover</div>
                      <div className="ob-record-item-value">{formData.turnover}</div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Status</div>
                      <div className="ob-record-item-value">{formData.cleanStatus}</div>
                    </div>
                    <div className="ob-record-item">
                      <div className="ob-record-item-label">Email</div>
                      <div className="ob-record-item-value">{formData.email}</div>
                    </div>
                    {formData.comms && formData.comms !== 'No Comments' && (
                      <div className="ob-record-item" style={{ gridColumn: '1 / -1' }}>
                        <div className="ob-record-item-label">Comments</div>
                        <div className="ob-record-item-value">{formData.comms}</div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {showSummary && (
              <label className="ob-checkbox-row" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#e2e8f0', fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={emailReport}
                  onChange={(e) => setEmailReport(e.target.checked)}
                  style={{ accentColor: ACCENT }}
                />
                Email PDF request to {formData.email}
              </label>
            )}

            {/* ─── Scroll anchor ─── */}
            <div ref={lastSectionRef} />

            {/* ─── Submit ─── */}
            <button
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              className="ob-btn ob-btn-submit"
            >
              {isSubmitting ? (
                <span className="ob-spinner">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Submitting…
                </span>
              ) : (
                'Submit Request'
              )}
            </button>

          </div>
        </div>

        <p className="ob-footer">IGLU South Yarra</p>
      </div>
    </div>
  );
};

export default NewDeparture;